import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";
import { FolderKanban, ShieldAlert, AlertOctagon, TrendingUp, Layers, Briefcase, GitBranch, ShieldCheck, ArrowRight, CalendarClock, History, CheckCircle2, AlertTriangle, Flag } from "lucide-react";
import api from "@/lib/api";
import { Shell, Empty } from "@/components/Shell";
import { RagBadge } from "@/components/Rag";
import { Select } from "@/pages/Projects";
import { useEntitlements } from "@/context/EntitlementsContext";

const RAG_COLORS = { Red: "#c62828", Amber: "#e6a100", Green: "#1f8a4c" };

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div data-testid={testId} className="panel flex items-center gap-4 p-5 transition-shadow duration-200 hover:shadow-md">
    <div className="rounded-md p-2.5" style={{ backgroundColor: "color-mix(in srgb, var(--brand-primary) 10%, white)" }}>
      <Icon size={20} style={{ color: "var(--brand-primary)" }} />
    </div>
    <div>
      <div className="font-display text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    </div>
  </div>
);

const AREA_LABELS = {
  resource_rag: "Resource",
  finance_rag: "Finance",
  benefits_rag: "Benefits",
  vendor_rag: "Vendor",
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [portfolios, setPortfolios] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [filters, setFilters] = useState({ portfolio_id: "", programme_id: "" });
  const [compliance, setCompliance] = useState(null);
  const navigate = useNavigate();
  const { can, loading: plansLoading } = useEntitlements();
  const fortnight = data?.fortnight_milestones;

  useEffect(() => {
    api.get("/portfolios").then(({ data }) => setPortfolios(data));
    api.get("/programmes").then(({ data }) => setProgrammes(data));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.portfolio_id) params.set("portfolio_id", filters.portfolio_id);
    if (filters.programme_id) params.set("programme_id", filters.programme_id);
    const qs = params.toString();
    api.get(`/dashboard/summary${qs ? `?${qs}` : ""}`).then(({ data }) => setData(data));
    // Wait for the plan before asking for a gated resource, otherwise the optimistic default
    // fires a request the plan does not allow.
    if (plansLoading) return;
    if (can("compliance_scorecards")) {
      api.get(`/compliance/scorecard${qs ? `?${qs}` : ""}`)
        .then(({ data }) => setCompliance(data.summary))
        .catch(() => setCompliance(null));
    } else {
      setCompliance(null);
    }
  }, [filters, can, plansLoading]);

  const c = data?.counts;
  const pie = data ? Object.entries(data.project_rag).map(([name, value]) => ({ name, value })) : [];
  const hasPie = pie.some((p) => p.value > 0);

  return (
    <Shell
      testId="dashboard-page"
      title="Portfolio Dashboard"
      subtitle="RAG rollup across all portfolios, programmes and projects"
      actions={
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-52">
            <label className="field-label">Portfolio</label>
            <Select testId="dashboard-portfolio-filter" value={filters.portfolio_id} onChange={(v) => setFilters((f) => ({ ...f, portfolio_id: v }))} options={portfolios.map((p) => ({ value: p.id, label: p.name }))} placeholder="All portfolios" />
          </div>
          <div className="w-52">
            <label className="field-label">Programme</label>
            <Select testId="dashboard-programme-filter" value={filters.programme_id} onChange={(v) => setFilters((f) => ({ ...f, programme_id: v }))} options={programmes.map((p) => ({ value: p.id, label: p.name }))} placeholder="All programmes" />
          </div>
          {(filters.portfolio_id || filters.programme_id) && (
            <button data-testid="clear-dashboard-filters" onClick={() => setFilters({ portfolio_id: "", programme_id: "" })} className="pb-2 text-xs font-semibold text-slate-500 underline hover:text-slate-800">
              Clear
            </button>
          )}
        </div>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-7">
        <Stat icon={FolderKanban} label="Projects" value={c?.projects ?? "—"} testId="stat-projects" />
        <Stat icon={Layers} label="Programmes" value={c?.programmes ?? "—"} testId="stat-programmes" />
        <Stat icon={Briefcase} label="Portfolios" value={c?.portfolios ?? "—"} testId="stat-portfolios" />
        <Stat icon={ShieldAlert} label="Open Risks" value={c?.open_risks ?? "—"} testId="stat-risks" />
        <Stat icon={AlertOctagon} label="Open Issues" value={c?.open_issues ?? "—"} testId="stat-issues" />
        <Stat icon={GitBranch} label="Open Deps" value={c?.open_dependencies ?? "—"} testId="stat-dependencies" />
        <Stat icon={TrendingUp} label="Avg % Complete" value={c ? `${c.avg_percent_complete}%` : "—"} testId="stat-complete" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {compliance && (
          <button
            data-testid="compliance-tile"
            onClick={() => navigate("/compliance")}
            className="panel group p-5 text-left transition-shadow duration-200 hover:shadow-md lg:col-span-3"
          >
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="rounded-md p-2.5" style={{ backgroundColor: "color-mix(in srgb, var(--brand-primary) 10%, white)" }}>
                  <ShieldCheck size={20} style={{ color: "var(--brand-primary)" }} />
                </div>
                <div>
                  <h3 className="font-display text-base font-semibold text-slate-800">Framework Compliance</h3>
                  <p className="text-xs text-slate-500">
                    {compliance.projects_assessed} assessed · {compliance.projects_unassessed} without a framework template
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div>
                  <div data-testid="compliance-tile-score" className="font-display text-2xl font-bold text-slate-900">{compliance.average_score}%</div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500">Average</div>
                </div>
                <div className="flex items-center gap-2">
                  <RagBadge value="Red" testId="compliance-tile-red" /><span className="text-sm font-semibold text-slate-700">{compliance.rag.Red}</span>
                  <RagBadge value="Amber" testId="compliance-tile-amber" /><span className="text-sm font-semibold text-slate-700">{compliance.rag.Amber}</span>
                  <RagBadge value="Green" testId="compliance-tile-green" /><span className="text-sm font-semibold text-slate-700">{compliance.rag.Green}</span>
                </div>
                <span className="flex items-center gap-1 text-sm font-semibold" style={{ color: "var(--brand-primary)" }}>
                  Open scorecard <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </div>
          </button>
        )}
        <div className="panel p-5" data-testid="rag-pie-panel">
          <h3 className="font-display text-base font-semibold text-slate-800">Project Health</h3>
          {hasPie ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" innerRadius={52} outerRadius={82} paddingAngle={2}>
                  {pie.map((p) => (
                    <Cell key={p.name} fill={RAG_COLORS[p.name]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="py-16 text-center text-sm text-slate-400">No projects yet.</p>
          )}
        </div>

        <div className="panel p-5 lg:col-span-2" data-testid="portfolio-bar-panel">
          <h3 className="font-display text-base font-semibold text-slate-800">RAG by Portfolio</h3>
          {data?.by_portfolio?.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.by_portfolio}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e8ecf1" vertical={false} />
                <XAxis dataKey="portfolio" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Green" stackId="a" fill={RAG_COLORS.Green} />
                <Bar dataKey="Amber" stackId="a" fill={RAG_COLORS.Amber} />
                <Bar dataKey="Red" stackId="a" fill={RAG_COLORS.Red} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="py-16 text-center text-sm text-slate-400">No portfolio data yet.</p>
          )}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="panel p-5" data-testid="area-rag-panel">
          <h3 className="font-display text-base font-semibold text-slate-800">Area RAG (latest status updates)</h3>
          <div className="mt-4 space-y-3">
            {Object.entries(AREA_LABELS).map(([k, label]) => {
              const v = data?.area_rag?.[k] || { Red: 0, Amber: 0, Green: 0 };
              const total = v.Red + v.Amber + v.Green;
              return (
                <div key={k} className="flex items-center gap-4">
                  <div className="w-20 text-xs font-semibold text-slate-600">{label}</div>
                  <div className="flex h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                    {["Green", "Amber", "Red"].map((r) =>
                      v[r] ? (
                        <div key={r} style={{ width: `${(v[r] / total) * 100}%`, backgroundColor: RAG_COLORS[r] }} />
                      ) : null,
                    )}
                  </div>
                  <div className="w-10 text-right text-xs text-slate-400">{total}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="panel p-5" data-testid="recent-projects-panel">
          <h3 className="font-display text-base font-semibold text-slate-800">Recent Projects</h3>
          {data?.recent_projects?.length ? (
            <div className="mt-3 divide-y divide-slate-100">
              {data.recent_projects.map((p) => (
                <button
                  key={p.id}
                  data-testid={`dash-project-${p.id}`}
                  onClick={() => navigate(`/projects/${p.id}`)}
                  className="flex w-full items-center justify-between py-2.5 text-left transition-colors hover:bg-slate-50"
                >
                  <div>
                    <div className="text-sm font-medium text-slate-800">{p.name}</div>
                    <div className="text-xs text-slate-400">
                      {p.stage || "No stage"} · {p.percent_complete || 0}% complete
                    </div>
                  </div>
                  <RagBadge value={p.rag} />
                </button>
              ))}
            </div>
          ) : (
            <Empty testId="no-recent-projects" text="No projects yet — create one from the Delivery tab." />
          )}
        </div>
      </div>

      {data?.not_reported?.length > 0 && (
        <div data-testid="dash-not-reported" className="mt-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span className="font-semibold">No current weekly status update:</span> {data.not_reported.join(", ")}
        </div>
      )}

      <div className="panel mt-6 p-5" data-testid="dash-fortnight-panel">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-base font-semibold text-slate-800">Milestones — a fortnight either side of today</h3>
            <p className="mt-0.5 text-xs text-slate-500">
              {fortnight?.window ? `${fortnight.window[0]} to ${fortnight.window[1]}` : ""}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold">
            <span data-testid="fortnight-count-upcoming" className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-600">
              {fortnight?.counts?.upcoming ?? 0} due next 2 weeks
            </span>
            <span data-testid="fortnight-count-achieved" className="rounded-full bg-emerald-50 px-2.5 py-1 text-emerald-700">
              {fortnight?.counts?.achieved ?? 0} achieved
            </span>
            <span data-testid="fortnight-count-overdue" className="rounded-full bg-red-50 px-2.5 py-1 text-red-700">
              {fortnight?.counts?.overdue ?? 0} passed without completion
            </span>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {[
            { key: "upcoming", title: "Due in the next 2 weeks", icon: CalendarClock,
              empty: "Nothing falls due in the next two weeks.", testId: "fortnight-upcoming" },
            { key: "recent", title: "Passed in the last 2 weeks", icon: History,
              empty: "No milestones were due in the last two weeks.", testId: "fortnight-recent" },
          ].map(({ key, title, icon: Icon, empty, testId }) => (
            <div key={key} data-testid={testId}>
              <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-slate-500">
                <Icon size={12} /> {title}
              </p>
              {fortnight?.[key]?.length ? (
                <div className="mt-2 divide-y divide-slate-100">
                  {fortnight[key].map((m) => (
                    <button
                      key={m.id}
                      data-testid={`fortnight-milestone-${m.id}`}
                      onClick={() => navigate(`/projects/${m.project_id}`)}
                      className="flex w-full items-start justify-between gap-3 py-2.5 text-left transition-colors hover:bg-slate-50"
                    >
                      <span className="min-w-0">
                        <span className="flex items-center gap-1.5">
                          {m.status === "Complete" ? (
                            <CheckCircle2 size={13} className="shrink-0 text-emerald-600" />
                          ) : m.overdue ? (
                            <AlertTriangle size={13} className="shrink-0 text-red-600" />
                          ) : (
                            <Flag size={13} className="shrink-0 text-slate-400" />
                          )}
                          <span className="truncate text-sm font-medium text-slate-800">{m.title}</span>
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-slate-500">
                          {m.project_name} · {m.milestone_type}
                          {m.slip_days ? ` · ${m.slip_days > 0 ? `${m.slip_days}d slip` : `${Math.abs(m.slip_days)}d early`}` : ""}
                        </span>
                      </span>
                      <span className="shrink-0 text-right">
                        <span className="block text-xs font-semibold text-slate-700">{m.due_date}</span>
                        <span className={`text-[11px] ${m.overdue ? "font-semibold text-red-600" : "text-slate-400"}`}>
                          {m.days_out === 0 ? "today"
                            : m.days_out > 0 ? `in ${m.days_out}d`
                            : `${Math.abs(m.days_out)}d ago`}
                          {m.status === "Complete" ? " · done" : m.overdue ? " · not complete" : ""}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <Empty testId={`no-${testId}`} text={empty} />
              )}
            </div>
          ))}
        </div>
      </div>


      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="panel p-5 lg:col-span-3" data-testid="dash-milestones-panel">
          <h3 className="font-display text-base font-semibold text-slate-800">
            Timeboxed Milestones{" "}
            <span className="text-xs font-normal text-slate-400">
              {data?.milestone_window ? `(${data.milestone_window[0]} to ${data.milestone_window[1]})` : ""}
            </span>
          </h3>
          {data?.milestones?.length ? (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>{["Project", "Milestone", "Type", "Baseline", "Forecast", "RAG", "Status"].map((h) => (
                    <th key={h} className="pb-2 pr-4 font-semibold">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.milestones.map((m) => (
                    <tr
                      key={m.id}
                      data-testid={`dash-milestone-${m.id}`}
                      onClick={() => navigate(`/projects/${m.project_id}`)}
                      className="cursor-pointer transition-colors hover:bg-slate-50"
                    >
                      <td className="py-2.5 pr-4 text-slate-700">{m.project_name}</td>
                      <td className="py-2.5 pr-4 font-medium text-slate-800">{m.title}</td>
                      <td className="py-2.5 pr-4 text-slate-600">{m.milestone_type}</td>
                      <td className="py-2.5 pr-4 text-slate-600">{m.baseline_date || "—"}</td>
                      <td className="py-2.5 pr-4 text-slate-600">{m.forecast_date || "—"}</td>
                      <td className="py-2.5 pr-4"><RagBadge value={m.rag} /></td>
                      <td className="py-2.5 pr-4 text-slate-600">{m.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Empty testId="no-dash-milestones" text="No milestones fall within 30 days either side of today." />
          )}
        </div>

        {[
          { key: "flagged_risks", title: "Risks on Weekly Reports", testId: "dash-risks-panel", empty: "No risks flagged for the weekly report." },
          { key: "flagged_issues", title: "Issues on Weekly Reports", testId: "dash-issues-panel", empty: "No issues flagged for the weekly report." },
        ].map((panel) => (
          <div key={panel.key} className="panel p-5" data-testid={panel.testId}>
            <h3 className="font-display text-base font-semibold text-slate-800">{panel.title}</h3>
            {data?.[panel.key]?.length ? (
              <div className="mt-3 divide-y divide-slate-100">
                {data[panel.key].map((r) => (
                  <button
                    key={r.id}
                    data-testid={`dash-${panel.key}-${r.id}`}
                    onClick={() => navigate(`/projects/${r.project_id}`)}
                    className="flex w-full items-start justify-between gap-3 py-2.5 text-left transition-colors hover:bg-slate-50"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800">{r.title}</div>
                      <div className="text-xs text-slate-400">
                        {r.ref ? `${r.ref} · ` : ""}{r.project_name} · {r.owner || "No owner"}
                      </div>
                    </div>
                    <RagBadge value={r.rag} />
                  </button>
                ))}
              </div>
            ) : (
              <Empty testId={`no-${panel.key}`} text={panel.empty} />
            )}
          </div>
        ))}
      </div>
    </Shell>
  );
}
